// Builds cross-domain navigation links between records that share an identifier
// (correlationId, traceId, as2MessageId, documentId) but live in different list/detail
// pages: AS2 transport messages, business documents/inbound messages, exchanges, and flow
// executions. Every link below either targets an existing detail route directly (documentId)
// or an existing list filter that the target page's query DTO already accepts — see
// apps/integration/docs/frontend-ux-audit-2026-08.md §4 items 3/6 and the cross-link gap
// note for which relationships do NOT have a filter to target yet.

export interface CrossLink {
  label: string;
  pathname: string;
  search: string;
}

function withParam(baseSearch: string, key: string, value: string): string {
  const params = new URLSearchParams(baseSearch);
  params.set(key, value);
  return `?${params.toString()}`;
}

export function documentDetailLink(documentId: string, search: string): CrossLink {
  return { label: 'View document', pathname: `/documents/${documentId}`, search };
}

export function documentsByCorrelationLink(correlationId: string, search: string): CrossLink {
  return { label: 'View documents', pathname: '/documents', search: withParam(search, 'correlationId', correlationId) };
}

export function documentsByTraceLink(traceId: string, search: string): CrossLink {
  return { label: 'View documents', pathname: '/documents', search: withParam(search, 'traceId', traceId) };
}

export function exchangesByCorrelationLink(correlationId: string, search: string): CrossLink {
  return { label: 'View exchanges', pathname: '/operations/exchanges', search: withParam(search, 'correlationId', correlationId) };
}

export function exchangesByTraceLink(traceId: string, search: string): CrossLink {
  return { label: 'View exchanges', pathname: '/operations/exchanges', search: withParam(search, 'traceId', traceId) };
}

export function executionsByCorrelationLink(correlationId: string, search: string): CrossLink {
  return { label: 'View executions', pathname: '/executions', search: withParam(search, 'correlationId', correlationId) };
}

// Business-level (parsed) inbound document, filtered by the AS2 transport message ID.
// InboundDocumentListQuery.as2MessageId — see types/api.ts.
export function inboundMessagesByAs2MessageIdLink(as2MessageId: string, search: string): CrossLink {
  return { label: 'View inbound message', pathname: '/inbound-messages', search: withParam(search, 'as2MessageId', as2MessageId) };
}

// AS2 transport-level inbound record, filtered by correlationId — the transport DTO itself
// has no correlationId field, but As2MessageListQuery.correlationId is accepted server-side
// (apps/as2-gateway InboundMessageController), so this direction only works starting from a
// record that already carries a correlationId (e.g. the business inbound document).
export function as2InboundTransportByCorrelationLink(correlationId: string, search: string): CrossLink {
  return { label: 'View AS2 transport message', pathname: '/as2/inbound-messages', search: withParam(search, 'correlationId', correlationId) };
}

// AS2 DLQ list, pre-filtered to the task type/partner of a specific outbound or inbound AS2
// message. There is no server-side filter (or frontend detail route) for a single DLQ task by
// its referenceId, so this narrows the list as much as the existing As2DlqListQuery supports —
// the caller is expected to have already confirmed a matching task exists via
// lib/as2DlqMatch.findMatchingAs2DlqTask before showing this link.
export function as2DlqTasksLink(taskType: string, partnerCode: string | undefined, search: string): CrossLink {
  const params = new URLSearchParams(search);
  params.set('taskType', taskType);
  if (partnerCode) params.set('partnerCode', partnerCode);
  return { label: 'View DLQ task', pathname: '/as2/dlq', search: `?${params.toString()}` };
}
